import React, { useMemo, useState, useRef } from 'react';
import { Line, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
// jsPDF and excel export utilities are imported dynamically in export functions
import html2canvas from 'html2canvas';
import IncidentClusterMap from './IncidentClusterMap';
import IncidentHeatmap from './IncidentHeatmap';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  BarElement,
  Tooltip,
  Legend,
);

// Helper functions - Always use Melbourne timezone
function getMonthYear(dateString) {
  const d = new Date(dateString);
  // Convert to Melbourne timezone for consistent month/year calculation
  const melbourneDate = new Date(d.toLocaleString('en-US', { timeZone: 'Australia/Melbourne' }));
  return `${melbourneDate.getFullYear()}-${String(melbourneDate.getMonth() + 1).padStart(2, '0')}`;
}

function getHourOfDay(dateString) {
  // Parse the date string and ensure it's treated as Melbourne time
  let d;
  if (dateString.endsWith('Z')) {
    // UTC date - convert to Melbourne time
    d = new Date(dateString);
  } else {
    // Local date - assume it's already in Melbourne time
    d = new Date(dateString);
  }

  // Always convert to Melbourne timezone for consistent hour calculation
  const melbourneDate = new Date(d.toLocaleString('en-US', { timeZone: 'Australia/Melbourne' }));
  const hour = melbourneDate.getHours();
  console.log(
    '🔍 DEBUG AdminAnalytics getHourOfDay:',
    dateString,
    '-> original:',
    d.getHours(),
    'melbourne:',
    hour,
  );
  return hour;
}

export default function AdminAnalytics({ reports, user }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [includePending, setIncludePending] = useState(false);
  const [mapView, setMapView] = useState('heatmap'); // 'clustering' or 'heatmap'

  // Refs for chart elements to capture for PDF
  const lineChartRef = useRef(null);
  const pieChartRef = useRef(null);
  const timeChartRef = useRef(null);
  const mapRef = useRef(null);

  // Only include approved reports in statistics, unless admin toggles pending
  const filtered = useMemo(() => {
    return reports.filter(r => {
      if (!includePending && !r.isApproved) return false;
      if (typeFilter !== 'all' && r.incidentType !== typeFilter) return false;
      if (dateRange.from && new Date(r.estimatedIncidentTime) < new Date(dateRange.from))
        return false;
      if (dateRange.to && new Date(r.estimatedIncidentTime) > new Date(dateRange.to)) return false;
      return true;
    });
  }, [reports, typeFilter, dateRange, includePending]);

  // Graph data (reuse logic from user graphs)
  const { lineData, pieData, stats, typeLabels } = useMemo(() => {
    if (!Array.isArray(filtered)) return { lineData: {}, pieData: {}, stats: {}, typeLabels: [] };
    const countsByMonth = {};
    const countsByType = {};
    filtered.forEach(r => {
      const month = getMonthYear(r.estimatedIncidentTime);
      countsByMonth[month] = (countsByMonth[month] || 0) + 1;
      if (r.incidentType) countsByType[r.incidentType] = (countsByType[r.incidentType] || 0) + 1;
    });
    const months = Object.keys(countsByMonth).sort();
    const lineData = {
      labels: months,
      datasets: [
        {
          label: 'Incident Reports',
          data: months.map(m => countsByMonth[m]),
          fill: false,
          borderColor: '#2563eb',
          backgroundColor: '#60a5fa',
          tension: 0.2,
        },
      ],
    };
    const typeLabels = Object.keys(countsByType);
    const pieData = {
      labels: typeLabels,
      datasets: [
        {
          data: typeLabels.map(t => countsByType[t]),
          backgroundColor: [
            '#2563eb',
            '#f59e42',
            '#10b981',
            '#f43f5e',
            '#a78bfa',
            '#fbbf24',
            '#6366f1',
            '#f87171',
            '#34d399',
            '#f472b6',
          ],
        },
      ],
    };
    const total = filtered.length;
    const mostCommonType = typeLabels.reduce(
      (a, b) => (countsByType[a] > countsByType[b] ? a : b),
      typeLabels[0] || '',
    );
    return {
      lineData,
      pieData,
      stats: {
        total,
        mostCommonType,
      },
      typeLabels,
    };
  }, [filtered]);

  // Color map for incident types (reuse from pie chart or define here)
  const typeColorMap = {
    'car-collision': '#2563eb',
    'multiple-vehicle-incident': '#1d4ed8',
    'car-pedestrian': '#f59e42',
    'car-bicycle': '#10b981',
    'car-motorcycle': '#f43f5e',
    'car-animal': '#a78bfa',
    'car-property': '#fbbf24',
    'car-rollover': '#6366f1',
    'car-fire': '#f87171',
    'motorcycle-collision': '#34d399',
    'motorcycle-car': '#f472b6',
    'traffic-light-malfunction': '#f59e0b',
    'traffic-light-damage': '#f97316',
    'road-hazard': '#6b7280',
    'debris-on-road': '#9ca3af',
    'pothole-or-surface-damage': '#6b7280',
    'signage-issue': '#94a3b8',
    'roadwork-issue': '#fb923c',
    'traffic-congestion-or-gridlock': '#facc15',
    'other-traffic-hazard': '#a1a1aa',
    // ... add more as needed, fallback below
  };
  const defaultColor = '#2563eb';
  const pointColor = typeFilter !== 'all' ? typeColorMap[typeFilter] || defaultColor : defaultColor;
  // Time of day distribution (24 bins)
  const timeOfDayData = useMemo(() => {
    const hours = Array(24).fill(0);
    filtered.forEach(r => {
      if (r.estimatedIncidentTime && !r.timeUnknown && !r.isDateRange) {
        try {
          const hour = getHourOfDay(r.estimatedIncidentTime);
          console.log(
            '🔍 DEBUG AdminAnalytics: Report',
            r.id,
            'date:',
            r.estimatedIncidentTime,
            'hour:',
            hour,
            'timeUnknown:',
            r.timeUnknown,
            'isDateRange:',
            r.isDateRange,
          );
          if (hour >= 0 && hour <= 23) {
            hours[hour]++;
          }
        } catch (error) {
          console.warn('Invalid date format for report:', r.id, r.estimatedIncidentTime);
        }
      } else {
        console.log(
          '🔍 DEBUG AdminAnalytics: Report',
          r.id,
          'EXCLUDED from time chart - timeUnknown:',
          r.timeUnknown,
          'isDateRange:',
          r.isDateRange,
        );
      }
    });

    // Simple timezone test
    const testDate = new Date('2025-10-15T16:00:00');
    const testMelbourne = new Date(
      testDate.toLocaleString('en-US', { timeZone: 'Australia/Melbourne' }),
    );
    console.log(
      '🔍 AdminAnalytics TIMEZONE TEST: 16:00 UTC should be',
      testMelbourne.getHours(),
      'in Melbourne (should be 2 or 3 depending on DST)',
    );

    return {
      labels: Array.from({ length: 24 }, (_, i) => {
        const hour = i;
        const period = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        return `${displayHour}:00 ${period}`;
      }),
      datasets: [
        {
          label: 'Incidents by Hour',
          data: hours,
          backgroundColor: pointColor,
          borderColor: pointColor,
          pointBackgroundColor: pointColor,
          pointBorderColor: pointColor,
          tension: 0.4,
          fill: false,
        },
      ],
    };
  }, [filtered, pointColor]);

  // Export handlers
  const exportToPDF = async () => {
    const { jsPDF } = await import('jspdf');
    const doc = new jsPDF('p', 'mm', 'a4');

    // Set up professional styling
    const pageWidth = doc.internal.pageSize.width;
    const margin = 20;
    const contentWidth = pageWidth - margin * 2;
    let yPosition = 30;

    // Helper function to add text with word wrapping
    const addWrappedText = (text, x, y, maxWidth, fontSize = 12, fontStyle = 'normal') => {
      doc.setFontSize(fontSize);
      doc.setFont(undefined, fontStyle);
      const lines = doc.splitTextToSize(text, maxWidth);
      doc.text(lines, x, y);
      return lines.length * (fontSize * 0.4); // Return height used
    };

    // Helper function to add a section header
    const addSectionHeader = (title, y) => {
      // Check if we need a new page for this section header
      y = checkPageBreak(y, 20); // Section header height

      doc.setFillColor(59, 130, 246); // Blue background
      doc.rect(margin, y - 5, contentWidth, 8, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text(title, margin + 5, y + 2);
      doc.setTextColor(0, 0, 0);
      return y + 15;
    };

    // Helper function to add a subsection
    const addSubsection = (title, y) => {
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(59, 130, 246);
      doc.text(title, margin, y);
      doc.setTextColor(0, 0, 0);
      return y + 8;
    };

    // Helper function to add a data row
    const addDataRow = (label, value, y) => {
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text(label, margin, y);
      doc.setFont(undefined, 'bold');
      doc.text(value, margin + 80, y);
      return y + 6;
    };

    // Helper function to capture and add a chart image
    const addChartImage = async (chartRef, title, y) => {
      if (!chartRef.current) {
        // Fallback to placeholder if chart ref is not available
        return addChartPlaceholder(title, y);
      }

      try {
        // Wait a bit for charts to render completely
        await new Promise(resolve => setTimeout(resolve, 500));

        // Capture the chart as canvas
        const canvas = await html2canvas(chartRef.current, {
          backgroundColor: '#ffffff',
          scale: 2, // Higher resolution
          useCORS: true,
          allowTaint: true,
          logging: false,
        });

        // Convert canvas to image data
        const imgData = canvas.toDataURL('image/png');

        // Calculate dimensions to fit in PDF
        const maxWidth = contentWidth;
        const maxHeight = 80; // Maximum height for charts
        const aspectRatio = canvas.width / canvas.height;

        let imgWidth = maxWidth;
        let imgHeight = imgWidth / aspectRatio;

        if (imgHeight > maxHeight) {
          imgHeight = maxHeight;
          imgWidth = imgHeight * aspectRatio;
        }

        // Check if we need a new page for this chart
        const totalHeight = 20 + imgHeight; // Title height + image height
        y = checkPageBreak(y, totalHeight);

        // Center the image horizontally
        const imgX = margin + (contentWidth - imgWidth) / 2;

        // Add chart title
        doc.setFontSize(10);
        doc.setTextColor(59, 130, 246);
        doc.setFont(undefined, 'bold');
        doc.text(title, margin, y);
        doc.setTextColor(0, 0, 0);

        // Add the chart image
        doc.addImage(imgData, 'PNG', imgX, y + 5, imgWidth, imgHeight);

        return y + imgHeight + 15;
      } catch (error) {
        console.error('Error capturing chart:', error);
        // Fallback to placeholder on error
        return addChartPlaceholder(title, y);
      }
    };

    // Helper function to check if we need a new page
    const checkPageBreak = (currentY, requiredHeight = 0) => {
      const pageHeight = doc.internal.pageSize.height;
      const safeMargin = 30; // Keep some space at bottom
      if (currentY + requiredHeight > pageHeight - safeMargin) {
        doc.addPage();
        return 30; // Reset to top of new page
      }
      return currentY;
    };

    // Helper function to add a chart placeholder (fallback)
    const addChartPlaceholder = (title, y) => {
      doc.setFillColor(240, 240, 240);
      doc.rect(margin, y, contentWidth, 40, 'F');
      doc.setDrawColor(200, 200, 200);
      doc.rect(margin, y, contentWidth, 40);

      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.setFont(undefined, 'italic');
      doc.text(title, margin + 5, y + 20);
      doc.text('Chart visualization would appear here', margin + 5, y + 30);
      doc.setTextColor(0, 0, 0);
      doc.setFont(undefined, 'normal');

      return y + 50;
    };

    // Helper function to add a map visualization placeholder
    const addMapPlaceholder = (title, y, mapType) => {
      doc.setFillColor(240, 240, 240);
      doc.rect(margin, y, contentWidth, 60, 'F');
      doc.setDrawColor(200, 200, 200);
      doc.rect(margin, y, contentWidth, 60);

      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.setFont(undefined, 'italic');
      doc.text(title, margin + 5, y + 15);
      doc.text(`${mapType} visualization would appear here`, margin + 5, y + 30);
      doc.text('Interactive maps are captured as static snapshots', margin + 5, y + 45);
      doc.setTextColor(0, 0, 0);
      doc.setFont(undefined, 'normal');

      return y + 70;
    };

    // Title Page
    doc.setFillColor(59, 130, 246);
    doc.rect(0, 0, pageWidth, 50, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont(undefined, 'bold');
    doc.text('SWARM Incident Reporter', pageWidth / 2, 25, { align: 'center' });
    doc.setFontSize(16);
    doc.text('Analytics Dashboard Report', pageWidth / 2, 40, { align: 'center' });

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(12);
    doc.setFont(undefined, 'normal');
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, margin, 70);
    doc.text(
      `Report Period: ${dateRange.from ? new Date(dateRange.from).toLocaleDateString() : 'All Time'} - ${dateRange.to ? new Date(dateRange.to).toLocaleDateString() : 'Present'}`,
      margin,
      80,
    );
    doc.text(
      `Map View: ${mapView === 'clustering' ? 'Incident Clustering' : 'Heatmap Analysis'}`,
      margin,
      90,
    );
    doc.text('Note: Interactive maps are available in the web dashboard', margin, 100);

    yPosition = 110;

    // Executive Summary
    yPosition = addSectionHeader('Executive Summary', yPosition);

    const totalReports = filtered.length;
    const approvedReports = filtered.filter(r => r.isApproved).length;
    const pendingReports = filtered.filter(r => !r.isApproved).length;
    const reportsWithCoords = filtered.filter(r => {
      if (!r.coordinates) return false;
      if (typeof r.coordinates === 'string') {
        return r.coordinates.trim() !== '';
      } else if (typeof r.coordinates === 'object' && r.coordinates.lat && r.coordinates.lng) {
        return true;
      }
      return false;
    }).length;

    yPosition = addSubsection('Key Metrics Overview', yPosition);
    yPosition = addDataRow('Total Incidents:', totalReports.toString(), yPosition);
    yPosition = addDataRow('Approved Reports:', approvedReports.toString(), yPosition);
    yPosition = addDataRow('Pending Review:', pendingReports.toString(), yPosition);
    yPosition = addDataRow('With Coordinates:', reportsWithCoords.toString(), yPosition);

    yPosition += 10;

    // Incident Type Analysis
    yPosition = addSectionHeader('Incident Type Analysis', yPosition);

    const typeCounts = {};
    filtered.forEach(r => {
      typeCounts[r.incidentType] = (typeCounts[r.incidentType] || 0) + 1;
    });

    const sortedTypes = Object.entries(typeCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5);

    yPosition = addSubsection('Top 5 Incident Types', yPosition);
    sortedTypes.forEach(([type, count]) => {
      yPosition = addDataRow(
        `${type.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}:`,
        count.toString(),
        yPosition,
      );
    });

    yPosition += 10;

    // Add incident type distribution chart
    yPosition = await addChartImage(pieChartRef, 'Incident Type Distribution', yPosition);

    yPosition += 10;

    // Location Analysis
    yPosition = addSectionHeader('Location Analysis', yPosition);

    // Initialize clustering variables at function level
    let clustersForRecommendations = [];
    let largestClusterForRecommendations = 0;

    if (reportsWithCoords > 0) {
      // Calculate clustering statistics
      const locationGroups = {};
      filtered
        .filter(r => {
          if (!r.coordinates) return false;
          if (typeof r.coordinates === 'string') {
            return r.coordinates.trim() !== '';
          } else if (typeof r.coordinates === 'object' && r.coordinates.lat && r.coordinates.lng) {
            return true;
          }
          return false;
        })
        .forEach(r => {
          let coords;
          if (typeof r.coordinates === 'string') {
            coords = r.coordinates.trim();
          } else if (typeof r.coordinates === 'object' && r.coordinates.lat && r.coordinates.lng) {
            coords = `${r.coordinates.lat}, ${r.coordinates.lng}`;
          } else {
            return; // Skip invalid coordinate formats
          }
          const match = coords.match(/(-?\d+\.?\d*),\s*(-?\d+\.?\d*)/);
          if (match) {
            const lat = Math.round(parseFloat(match[1]) * 1000) / 1000;
            const lng = Math.round(parseFloat(match[2]) * 1000) / 1000;
            const key = `${lat},${lng}`;
            if (!locationGroups[key]) {
              locationGroups[key] = [];
            }
            locationGroups[key].push(r);
          }
        });

      const clusters = Object.entries(locationGroups).filter(([, reports]) => reports.length > 1);
      const singleIncidents = Object.entries(locationGroups).filter(
        ([, reports]) => reports.length === 1,
      );
      const largestCluster =
        clusters.length > 0 ? Math.max(...clusters.map(([, reports]) => reports.length)) : 0;

      // Store for recommendations
      clustersForRecommendations = clusters;
      largestClusterForRecommendations = largestCluster;

      yPosition = addSubsection('Clustering Analysis', yPosition);
      yPosition = addDataRow('Total Clusters:', clusters.length.toString(), yPosition);
      yPosition = addDataRow('Single Incidents:', singleIncidents.length.toString(), yPosition);
      yPosition = addDataRow('Largest Cluster:', largestCluster.toString(), yPosition);
      yPosition = addDataRow(
        'Average Cluster Size:',
        clusters.length > 0 ? (reportsWithCoords / clusters.length).toFixed(1) : '0',
        yPosition,
      );

      yPosition += 10;

      // Add map visualization - use placeholder for complex interactive maps
      yPosition = addMapPlaceholder(
        `${mapView === 'clustering' ? 'Incident Clustering Map' : 'Incident Heatmap'} - ${reportsWithCoords} incidents with coordinates`,
        yPosition,
        mapView === 'clustering' ? 'Clustering' : 'Heatmap',
      );

      // Add separate map snapshots section
      yPosition += 10;
      yPosition = addSubsection('Map Visualizations', yPosition);

      // Add clustering map snapshot
      yPosition = addMapPlaceholder('Incident Clustering Analysis', yPosition, 'Clustering');

      yPosition += 5;

      // Add heatmap snapshot
      yPosition = addMapPlaceholder('Incident Density Heatmap', yPosition, 'Heatmap');
    } else {
      yPosition = addSubsection('Location Data', yPosition);
      yPosition = addWrappedText(
        'No incidents with coordinate data available for location analysis.',
        margin,
        yPosition,
        contentWidth,
      );
      yPosition += 15;
    }

    // Check if we need a new page before Time-based Analysis
    yPosition = checkPageBreak(yPosition, 150); // Increased space needed for map section

    // Time-based Analysis
    yPosition = addSectionHeader('Time-based Analysis', yPosition);

    const timeDistribution = {};
    filtered.forEach(r => {
      const date = new Date(r.estimatedIncidentTime);
      const monthYear = `${date.getMonth() + 1}/${date.getFullYear()}`;
      timeDistribution[monthYear] = (timeDistribution[monthYear] || 0) + 1;
    });

    const sortedMonths = Object.entries(timeDistribution)
      .sort(([a], [b]) => {
        const [aMonth, aYear] = a.split('/');
        const [bMonth, bYear] = b.split('/');
        return new Date(aYear, aMonth - 1) - new Date(bYear, bMonth - 1);
      })
      .slice(-6); // Last 6 months

    yPosition = addSubsection('Monthly Incident Trends (Last 6 Months)', yPosition);
    sortedMonths.forEach(([monthYear, count]) => {
      yPosition = addDataRow(`${monthYear}:`, count.toString(), yPosition);
    });

    yPosition += 10;

    // Add trend chart
    yPosition = await addChartImage(lineChartRef, 'Monthly Incident Trends Chart', yPosition);

    yPosition += 10;

    // Add time of day distribution chart
    yPosition = await addChartImage(timeChartRef, 'Time of Day Distribution', yPosition);

    // Severity Analysis
    yPosition = addSectionHeader('Severity Analysis', yPosition);

    const severityWeights = {
      'car-collision': 1.0,
      'multiple-vehicle-incident': 1.3,
      'car-pedestrian': 1.5,
      'car-bicycle': 1.2,
      'car-motorcycle': 1.3,
      'car-animal': 0.8,
      'car-property': 0.7,
      'car-rollover': 1.4,
      'car-fire': 1.6,
      'motorcycle-collision': 1.3,
      'motorcycle-car': 1.3,
      'truck-collision': 1.4,
      'bicycle-collision': 1.1,
      'pedestrian-collision': 1.5,
      'near-miss': 0.9,
      'near-miss-car': 0.9,
      'near-miss-motorcycle': 0.9,
      'near-miss-truck': 0.9,
      'near-miss-bicycle': 0.9,
      'near-miss-pedestrian': 0.9,
      'dangerous-driving': 1.2,
      'dangerous-driving-car': 1.2,
      'dangerous-driving-motorcycle': 1.2,
      'dangerous-driving-truck': 1.2,
      'dangerous-driving-bicycle': 1.2,
      'traffic-light-malfunction': 0.7,
      'traffic-light-damage': 0.7,
      'road-hazard': 0.8,
      'debris-on-road': 0.7,
      'pothole-or-surface-damage': 0.7,
      'signage-issue': 0.7,
      'roadwork-issue': 0.7,
      'traffic-congestion-or-gridlock': 0.7,
      'other-traffic-hazard': 0.7,
    };

    const severityScores = filtered.map(r => ({
      incident: r,
      score: severityWeights[r.incidentType] || 1.0,
    }));

    const highSeverity = severityScores.filter(s => s.score >= 1.4);
    const mediumSeverity = severityScores.filter(s => s.score >= 1.0 && s.score < 1.4);
    const lowSeverity = severityScores.filter(s => s.score < 1.0);

    yPosition = addSubsection('Severity Distribution', yPosition);
    yPosition = addDataRow('High Severity (≥1.4):', highSeverity.length.toString(), yPosition);
    yPosition = addDataRow(
      'Medium Severity (1.0-1.4):',
      mediumSeverity.length.toString(),
      yPosition,
    );
    yPosition = addDataRow('Low Severity (<1.0):', lowSeverity.length.toString(), yPosition);

    yPosition += 10;

    // Recommendations
    yPosition = addSectionHeader('Recommendations & Insights', yPosition);

    yPosition = addSubsection('Key Findings', yPosition);
    const findings = [];

    if (clustersForRecommendations && clustersForRecommendations.length > 0) {
      findings.push(
        `• ${clustersForRecommendations.length} incident clusters identified, indicating potential problem areas`,
      );
      if (largestClusterForRecommendations > 5) {
        findings.push(
          `• Largest cluster has ${largestClusterForRecommendations} incidents, requiring immediate attention`,
        );
      }
    }

    if (highSeverity.length > 0) {
      findings.push(
        `• ${highSeverity.length} high-severity incidents detected, prioritizing safety measures`,
      );
    }

    if (timeDistribution && Object.keys(timeDistribution).length > 0) {
      const recentMonths = Object.entries(timeDistribution).slice(-2);
      const trend =
        recentMonths[1] && recentMonths[0] ? recentMonths[1][1] - recentMonths[0][1] : 0;
      if (trend > 0) {
        findings.push('• Increasing incident trend detected in recent months');
      } else if (trend < 0) {
        findings.push('• Decreasing incident trend observed, safety measures showing effect');
      }
    }

    if (findings.length === 0) {
      findings.push('• Insufficient data for detailed analysis');
    }

    findings.forEach(finding => {
      yPosition = addWrappedText(finding, margin + 5, yPosition, contentWidth - 5, 10);
      yPosition += 6;
    });

    yPosition += 10;

    // Check if we need a new page before footer
    if (yPosition > 250) {
      doc.addPage();
      yPosition = 30;
    }

    // Footer
    const footerY = doc.internal.pageSize.height - 20;
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text(
      'SWARM Incident Reporter - Safer Walking and Riding for Murray Rd',
      pageWidth / 2,
      footerY,
      { align: 'center' },
    );
    doc.text(
      'This report was automatically generated from the analytics dashboard',
      pageWidth / 2,
      footerY + 5,
      { align: 'center' },
    );

    // Save the PDF
    doc.save(`swarm-analytics-report-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const exportXLSX = async () => {
    const { createWorkbook, addSheetFromRows, downloadWorkbook } = await import(
      '../utils/excelExport'
    );

    // Prepare data for different sheets
    const reportsWithCoords = filtered.filter(r => {
      if (!r.coordinates) return false;
      if (typeof r.coordinates === 'string') {
        return r.coordinates.trim() !== '';
      } else if (typeof r.coordinates === 'object' && r.coordinates.lat && r.coordinates.lng) {
        return true;
      }
      return false;
    });

    // Calculate clustering statistics
    const locationGroups = {};
    reportsWithCoords.forEach(r => {
      let coords;
      if (typeof r.coordinates === 'string') {
        coords = r.coordinates.trim();
      } else if (typeof r.coordinates === 'object' && r.coordinates.lat && r.coordinates.lng) {
        coords = `${r.coordinates.lat}, ${r.coordinates.lng}`;
      } else {
        return; // Skip invalid coordinate formats
      }
      const match = coords.match(/(-?\d+\.?\d*),\s*(-?\d+\.?\d*)/);
      if (match) {
        const lat = Math.round(parseFloat(match[1]) * 1000) / 1000;
        const lng = Math.round(parseFloat(match[2]) * 1000) / 1000;
        const key = `${lat},${lng}`;
        if (!locationGroups[key]) {
          locationGroups[key] = [];
        }
        locationGroups[key].push(r);
      }
    });

    const clusters = Object.entries(locationGroups).filter(([, reports]) => reports.length > 1);
    const singleIncidents = Object.entries(locationGroups).filter(
      ([, reports]) => reports.length === 1,
    );

    // Severity analysis
    const severityWeights = {
      'car-collision': 1.0,
      'multiple-vehicle-incident': 1.3,
      'car-pedestrian': 1.5,
      'car-bicycle': 1.2,
      'car-motorcycle': 1.3,
      'car-animal': 0.8,
      'car-property': 0.7,
      'car-rollover': 1.4,
      'car-fire': 1.6,
      'motorcycle-collision': 1.3,
      'motorcycle-car': 1.3,
      'truck-collision': 1.4,
      'bicycle-collision': 1.1,
      'pedestrian-collision': 1.5,
      'near-miss': 0.9,
      'near-miss-car': 0.9,
      'near-miss-motorcycle': 0.9,
      'near-miss-truck': 0.9,
      'near-miss-bicycle': 0.9,
      'near-miss-pedestrian': 0.9,
      'dangerous-driving': 1.2,
      'dangerous-driving-car': 1.2,
      'dangerous-driving-motorcycle': 1.2,
      'dangerous-driving-truck': 1.2,
      'dangerous-driving-bicycle': 1.2,
      'traffic-light-malfunction': 0.7,
      'traffic-light-damage': 0.7,
      'road-hazard': 0.8,
      'debris-on-road': 0.7,
      'pothole-or-surface-damage': 0.7,
      'signage-issue': 0.7,
      'roadwork-issue': 0.7,
      'traffic-congestion-or-gridlock': 0.7,
      'other-traffic-hazard': 0.7,
    };

    const severityScores = filtered.map(r => ({
      incident: r,
      score: severityWeights[r.incidentType] || 1.0,
    }));

    // Time-based analysis
    const timeDistribution = {};
    filtered.forEach(r => {
      const date = new Date(r.estimatedIncidentTime);
      const monthYear = `${date.getMonth() + 1}/${date.getFullYear()}`;
      timeDistribution[monthYear] = (timeDistribution[monthYear] || 0) + 1;
    });

    const workbook = createWorkbook();

    // 1. Summary Sheet
    const summaryData = [
      ['SWARM Incident Reporter - Analytics Summary'],
      [''],
      ['Report Information'],
      [
        'Generated On',
        new Date().toLocaleDateString('en-AU', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          timeZone: 'Australia/Melbourne',
        }),
      ],
      [
        'Report Period',
        `${
          dateRange.from
            ? new Date(dateRange.from).toLocaleDateString('en-AU', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                timeZone: 'Australia/Melbourne',
              })
            : 'All Time'
        } - ${
          dateRange.to
            ? new Date(dateRange.to).toLocaleDateString('en-AU', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                timeZone: 'Australia/Melbourne',
              })
            : 'Present'
        }`,
      ],
      ['Map View', mapView === 'clustering' ? 'Incident Clustering' : 'Heatmap Analysis'],
      [''],
      ['Key Metrics'],
      ['Total Incidents', filtered.length],
      ['Approved Reports', filtered.filter(r => r.isApproved).length],
      ['Pending Review', filtered.filter(r => !r.isApproved).length],
      ['With Coordinates', reportsWithCoords.length],
      ['Total Clusters', clusters.length],
      ['Single Incidents', singleIncidents.length],
      [''],
      ['Severity Distribution'],
      ['High Severity (≥1.4)', severityScores.filter(s => s.score >= 1.4).length],
      [
        'Medium Severity (1.0-1.4)',
        severityScores.filter(s => s.score >= 1.0 && s.score < 1.4).length,
      ],
      ['Low Severity (<1.0)', severityScores.filter(s => s.score < 1.0).length],
    ];

    addSheetFromRows(workbook, 'Summary', summaryData);

    // 2. Incident Details Sheet
    const incidentData = filtered.map(r => [
      r.id,
      r.incidentType,
      r.location,
      r.description,
      new Date(r.estimatedIncidentTime).toLocaleDateString('en-AU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        timeZone: 'Australia/Melbourne',
      }),
      r.isApproved ? 'Approved' : 'Pending',
      r.coordinates || 'No coordinates',
      severityWeights[r.incidentType] || 1.0,
      r.reportedBy || 'Anonymous',
    ]);

    const incidentHeaders = [
      'ID',
      'Incident Type',
      'Location',
      'Description',
      'Date',
      'Status',
      'Coordinates',
      'Severity Score',
      'Reported By',
    ];

    addSheetFromRows(workbook, 'Incident Details', [incidentHeaders, ...incidentData]);

    // 3. Location Analysis Sheet
    const locationData = [
      ['Location Analysis - Clustering Results'],
      [''],
      ['Cluster Information'],
      ['Coordinates', 'Incident Count', 'Incident Types', 'Severity Range', 'Date Range'],
    ];

    clusters.forEach(([coords, reports]) => {
      const types = [...new Set(reports.map(r => r.incidentType))].join(', ');
      const severityRange = `${Math.min(...reports.map(r => severityWeights[r.incidentType] || 1.0)).toFixed(1)} - ${Math.max(...reports.map(r => severityWeights[r.incidentType] || 1.0)).toFixed(1)}`;
      const dates = reports.map(r => new Date(r.estimatedIncidentTime));
      const minDate = new Date(Math.min(...dates));
      const maxDate = new Date(Math.max(...dates));
      const dateRange = `${minDate.toLocaleDateString()} - ${maxDate.toDateString()}`;

      locationData.push([coords, reports.length, types, severityRange, dateRange]);
    });

    locationData.push(['']);
    locationData.push(['Single Incidents']);
    locationData.push(['Coordinates', 'Incident Type', 'Severity Score', 'Date']);

    singleIncidents.forEach(([coords, reports]) => {
      reports.forEach(report => {
        locationData.push([
          coords,
          report.incidentType,
          severityWeights[report.incidentType] || 1.0,
          new Date(report.estimatedIncidentTime).toLocaleDateString(),
        ]);
      });
    });

    addSheetFromRows(workbook, 'Location Analysis', locationData);

    // 4. Incident Type Analysis Sheet
    const typeCounts = {};
    filtered.forEach(r => {
      typeCounts[r.incidentType] = (typeCounts[r.incidentType] || 0) + 1;
    });

    const typeData = [
      ['Incident Type Analysis'],
      [''],
      ['Incident Type', 'Count', 'Percentage', 'Severity Weight', 'Risk Level'],
    ];

    const totalIncidents = filtered.length;
    Object.entries(typeCounts)
      .sort(([, a], [, b]) => b - a)
      .forEach(([type, count]) => {
        const percentage = ((count / totalIncidents) * 100).toFixed(1);
        const severity = severityWeights[type] || 1.0;
        let riskLevel = 'Low';
        if (severity >= 1.4) riskLevel = 'High';
        else if (severity >= 1.0) riskLevel = 'Medium';

        typeData.push([type, count, `${percentage}%`, severity, riskLevel]);
      });

    addSheetFromRows(workbook, 'Incident Type Analysis', typeData);

    // 5. Time-based Analysis Sheet
    const sortedMonths = Object.entries(timeDistribution).sort(([a], [b]) => {
      const [aMonth, aYear] = a.split('/');
      const [bMonth, bYear] = b.split('/');
      return new Date(aYear, aMonth - 1) - new Date(bYear, bMonth - 1);
    });

    const timeData = [
      ['Time-based Analysis - Monthly Trends'],
      [''],
      ['Month/Year', 'Incident Count', 'Cumulative Total', 'Month-over-Month Change'],
    ];

    let cumulative = 0;
    sortedMonths.forEach(([monthYear, count], index) => {
      cumulative += count;
      const prevCount = index > 0 ? sortedMonths[index - 1][1] : 0;
      const change = index > 0 ? count - prevCount : 0;
      const changeText = index > 0 ? (change > 0 ? `+${change}` : change.toString()) : 'N/A';

      timeData.push([monthYear, count, cumulative, changeText]);
    });

    addSheetFromRows(workbook, 'Time Analysis', timeData);

    // 6. Severity Analysis Sheet
    const severityData = [
      ['Severity Analysis - Incident Risk Assessment'],
      [''],
      ['Severity Level', 'Score Range', 'Incident Count', 'Percentage', 'Examples'],
    ];

    const highSeverity = severityScores.filter(s => s.score >= 1.4);
    const mediumSeverity = severityScores.filter(s => s.score >= 1.0 && s.score < 1.4);
    const lowSeverity = severityScores.filter(s => s.score < 1.0);

    severityData.push([
      'High Risk',
      '≥1.4',
      highSeverity.length,
      `${((highSeverity.length / totalIncidents) * 100).toFixed(1)}%`,
      'Car-pedestrian, Car-fire, Car-rollover',
    ]);
    severityData.push([
      'Medium Risk',
      '1.0-1.4',
      mediumSeverity.length,
      `${((mediumSeverity.length / totalIncidents) * 100).toFixed(1)}%`,
      'Car-collision, Car-motorcycle, Truck-collision',
    ]);
    severityData.push([
      'Low Risk',
      '<1.0',
      lowSeverity.length,
      `${((lowSeverity.length / totalIncidents) * 100).toFixed(1)}%`,
      'Car-animal, Car-property, Near-miss',
    ]);

    severityData.push(['']);
    severityData.push(['Detailed Severity Breakdown']);
    severityData.push(['Incident Type', 'Severity Score', 'Risk Level', 'Count']);

    Object.entries(severityWeights).forEach(([type, weight]) => {
      const count = typeCounts[type] || 0;
      let riskLevel = 'Low';
      if (weight >= 1.4) riskLevel = 'High';
      else if (weight >= 1.0) riskLevel = 'Medium';

      severityData.push([type, weight, riskLevel, count]);
    });

    addSheetFromRows(workbook, 'Severity Analysis', severityData);

    // 7. Recommendations Sheet
    const recommendationsData = [
      ['Recommendations & Insights'],
      [''],
      ['Analysis Category', 'Finding', 'Recommendation', 'Priority'],
    ];

    // Add clustering recommendations
    if (clusters.length > 0) {
      const largestCluster = Math.max(...clusters.map(([, reports]) => reports.length));
      if (largestCluster > 5) {
        recommendationsData.push([
          'Location Clustering',
          `${largestCluster} incidents in largest cluster`,
          'Immediate safety review and intervention required',
          'High',
        ]);
      }
      recommendationsData.push([
        'Location Clustering',
        `${clusters.length} incident clusters identified`,
        'Implement targeted safety measures in cluster areas',
        'Medium',
      ]);
    }

    // Add severity recommendations
    if (highSeverity.length > 0) {
      recommendationsData.push([
        'Severity Analysis',
        `${highSeverity.length} high-severity incidents`,
        'Prioritize safety measures for high-risk incident types',
        'High',
      ]);
    }

    // Add trend recommendations
    if (sortedMonths.length >= 2) {
      const recentMonths = sortedMonths.slice(-2);
      const trend = recentMonths[1][1] - recentMonths[0][1];
      if (trend > 0) {
        recommendationsData.push([
          'Trend Analysis',
          'Increasing incident trend detected',
          'Review and strengthen current safety measures',
          'Medium',
        ]);
      } else if (trend < 0) {
        recommendationsData.push([
          'Trend Analysis',
          'Decreasing incident trend observed',
          'Continue current safety measures and monitor effectiveness',
          'Low',
        ]);
      }
    }

    // Add general recommendations
    recommendationsData.push([
      'Data Quality',
      `${reportsWithCoords}/${totalIncidents} incidents have coordinates`,
      'Encourage users to provide location data for better analysis',
      'Medium',
    ]);

    addSheetFromRows(workbook, 'Recommendations', recommendationsData);

    await downloadWorkbook(
      workbook,
      `swarm-analytics-export-${new Date().toISOString().split('T')[0]}.xlsx`,
    );
  };

  return (
    <section className="section" aria-labelledby="admin-analytics-heading">
      <div className="flex flex-row w-full">
        {/* Collapsible Sidebar */}
        <aside
          className={`transition-all duration-300 ${sidebarOpen ? 'w-64' : 'w-10'} bg-gray-50 border border-gray-100 rounded-md p-2 mr-4 flex flex-col items-stretch`}
          style={{ minWidth: sidebarOpen ? 200 : 40 }}
        >
          <button
            className="mb-2 self-end btn btn-xs btn-secondary px-2 py-1"
            aria-label={sidebarOpen ? 'Collapse filters' : 'Expand filters'}
            onClick={() => setSidebarOpen(open => !open)}
          >
            {sidebarOpen ? <span>&#x25C0;</span> : <span>&#x25B6;</span>}
          </button>
          {sidebarOpen && (
            <>
              {(user?.isAdmin || user?.isSuperAdmin) && (
                <div className="flex items-center mb-3">
                  <input
                    id="toggle-pending"
                    type="checkbox"
                    checked={includePending}
                    onChange={e => setIncludePending(e.target.checked)}
                    className="mr-2"
                  />
                  <label
                    htmlFor="toggle-pending"
                    className="text-xs font-medium select-none cursor-pointer"
                  >
                    Include Pending Reports
                  </label>
                </div>
              )}
              <div className="flex flex-col mb-2">
                <label className="block text-xs font-medium mb-0.5">Type</label>
                <select
                  value={typeFilter}
                  onChange={e => setTypeFilter(e.target.value)}
                  className="form-input form-input-xs text-xs py-1 px-2 h-8 min-w-[120px]"
                >
                  <option value="all">All Types</option>
                  {typeLabels.map(t => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col mb-2">
                <label className="block text-xs font-medium mb-0.5">From</label>
                <input
                  type="date"
                  value={dateRange.from}
                  onChange={e => setDateRange(r => ({ ...r, from: e.target.value }))}
                  className="form-input form-input-xs text-xs py-1 px-2 h-8 min-w-[110px]"
                />
              </div>
              <div className="flex flex-col mb-2">
                <label className="block text-xs font-medium mb-0.5">To</label>
                <input
                  type="date"
                  value={dateRange.to}
                  onChange={e => setDateRange(r => ({ ...r, to: e.target.value }))}
                  className="form-input form-input-xs text-xs py-1 px-2 h-8 min-w-[110px]"
                />
              </div>
              <div className="flex flex-row gap-1 mt-2">
                <button
                  onClick={exportToPDF}
                  className="btn btn-primary btn-small text-xs px-2 py-1"
                >
                  Export PDF
                </button>
                <button
                  onClick={exportXLSX}
                  className="btn btn-secondary btn-small text-xs px-2 py-1"
                >
                  Export XLSX
                </button>
              </div>
            </>
          )}
        </aside>
        {/* Main Graph/Chart Area */}
        <div className="flex-1">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div
              className="bg-white rounded-xl shadow-soft p-6 border border-gray-100"
              ref={lineChartRef}
            >
              <h3 className="text-lg font-semibold mb-4">Incident Reports Over Time</h3>
              <Line data={lineData} />
            </div>
            <div
              className="bg-white rounded-xl shadow-soft p-6 border border-gray-100"
              ref={pieChartRef}
            >
              <h3 className="text-lg font-semibold mb-4">Incident Type Distribution</h3>
              <Pie data={pieData} />
            </div>
          </div>
          {/* Time of Day Distribution */}
          <div
            className="mt-8 bg-white rounded-xl shadow-soft p-6 border border-gray-100"
            ref={timeChartRef}
          >
            <h3 className="text-lg font-semibold mb-4">Time of Day Distribution</h3>
            <Line
              data={timeOfDayData}
              options={{
                responsive: true,
                plugins: { legend: { display: false } },
                scales: {
                  x: { title: { display: true, text: 'Hour of Day' } },
                  y: {
                    title: { display: true, text: 'Number of Incidents' },
                    beginAtZero: true,
                    ticks: {
                      stepSize: 1,
                      callback(value) {
                        return Number.isInteger(value) ? value : null;
                      },
                    },
                  },
                },
              }}
            />
          </div>

          {/* Incident Clustering Map */}
          <div
            className="mt-8 bg-white rounded-xl shadow-soft p-6 border border-gray-100"
            ref={mapRef}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold">Incident Location Analysis</h3>
                <p className="text-sm text-gray-600">
                  {mapView === 'clustering'
                    ? 'This map shows the concentration of incidents around different locations. Clusters indicate areas with multiple incidents, helping identify problem zones.'
                    : 'This heatmap shows incident density with color intensity indicating concentration.'}
                </p>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setMapView('clustering')}
                  className={`px-3 py-1 text-xs rounded-md transition-colors ${
                    mapView === 'clustering'
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Clustering
                </button>
                <button
                  onClick={() => setMapView('heatmap')}
                  className={`px-3 py-1 text-xs rounded-md transition-colors ${
                    mapView === 'heatmap'
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Heatmap
                </button>
              </div>
            </div>

            {mapView === 'clustering' ? (
              <IncidentClusterMap
                reports={filtered}
                typeFilter={typeFilter}
                dateRange={dateRange}
                includePending={includePending}
              />
            ) : (
              <IncidentHeatmap
                reports={filtered}
                typeFilter={typeFilter}
                dateRange={dateRange}
                includePending={includePending}
              />
            )}
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-primary-50 rounded-xl p-6 text-center">
              <div className="text-3xl font-bold text-primary-700">{stats.total}</div>
              <div className="text-gray-700 mt-2">Total Reports (Filtered)</div>
            </div>
            <div className="bg-success-50 rounded-xl p-6 text-center">
              <div className="text-3xl font-bold text-success-700">
                {stats.mostCommonType || '—'}
              </div>
              <div className="text-gray-700 mt-2">Most Common Type</div>
            </div>
          </div>

          {/* Clustering Statistics */}
          {(() => {
            const reportsWithCoords = filtered.filter(r => {
              if (!r.coordinates) return false;
              // Handle both string and object coordinate formats
              if (typeof r.coordinates === 'string') {
                return r.coordinates.trim() !== '';
              } else if (
                typeof r.coordinates === 'object' &&
                r.coordinates.lat &&
                r.coordinates.lng
              ) {
                return true;
              }
              return false;
            });
            if (reportsWithCoords.length === 0) return null;

            // Group by approximate location for clustering analysis
            const locationGroups = {};
            reportsWithCoords.forEach(r => {
              let coords;
              // Handle both string and object coordinate formats
              if (typeof r.coordinates === 'string') {
                coords = r.coordinates.trim();
              } else if (
                typeof r.coordinates === 'object' &&
                r.coordinates.lat &&
                r.coordinates.lng
              ) {
                coords = `${r.coordinates.lat}, ${r.coordinates.lng}`;
              } else {
                return; // Skip invalid coordinate formats
              }

              const match = coords.match(/(-?\d+\.?\d*),\s*(-?\d+\.?\d*)/);
              if (match) {
                // Round to 3 decimal places (~100m precision) for clustering analysis
                const lat = Math.round(parseFloat(match[1]) * 1000) / 1000;
                const lng = Math.round(parseFloat(match[2]) * 1000) / 1000;
                const key = `${lat},${lng}`;
                if (!locationGroups[key]) {
                  locationGroups[key] = [];
                }
                locationGroups[key].push(r);
              }
            });

            const clusters = Object.entries(locationGroups).filter(
              ([_, reports]) => reports.length > 1,
            );
            const singleIncidents = Object.entries(locationGroups).filter(
              ([_, reports]) => reports.length === 1,
            );
            const largestCluster =
              clusters.length > 0 ? Math.max(...clusters.map(([_, reports]) => reports.length)) : 0;

            return (
              <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-blue-700">{reportsWithCoords.length}</div>
                  <div className="text-gray-700 text-sm mt-1">With Coordinates</div>
                </div>
                <div className="bg-purple-50 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-purple-700">{clusters.length}</div>
                  <div className="text-gray-700 text-sm mt-1">Clusters</div>
                </div>
                <div className="bg-orange-50 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-orange-700">{singleIncidents.length}</div>
                  <div className="text-gray-700 text-sm mt-1">Single Incidents</div>
                </div>
                <div className="bg-red-50 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-red-700">{largestCluster}</div>
                  <div className="text-gray-700 text-sm mt-1">Largest Cluster</div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </section>
  );
}
